import React from 'react';
import { useLocation } from 'react-router-dom';
import { useStoreMode } from '../../hooks/useStoreMode';
import SingleProductPage from '../../pages/SingleProductPage';

/**
 * Wraps the storefront routes. When the tenant is in 'single' mode, paths
 * that would normally render the catalog (home, product list, product
 * detail) are intercepted and replaced with `<SingleProductPage>`.
 *
 * Cart, checkout, account, and other utility routes pass through unchanged
 * so the buy flow stays intact.
 */
const SINGLE_MODE_INTERCEPT = (path: string): boolean => {
  if (path === '/' || path === '') return true;
  if (path === '/products' || path === '/products/') return true;
  if (/^\/products\/[^/]+\/?$/.test(path)) return true;
  if (/^\/p\/[^/]+\/?$/.test(path)) return true;
  return false;
};

interface Props {
  children: React.ReactNode;
}

export const SingleProductGate: React.FC<Props> = ({ children }) => {
  const location = useLocation();
  const { mode, productId, loading } = useStoreMode();

  // While the mode is resolving, fall through to the normal tree.
  // The catalog renders its own loading state; for single-mode tenants
  // the gate kicks in on the next render once mode lands.
  if (loading || mode !== 'single') {
    return <>{children}</>;
  }

  if (!productId) {
    // Mode says single but we don't know which product — fall through.
    return <>{children}</>;
  }

  if (!SINGLE_MODE_INTERCEPT(location.pathname)) {
    return <>{children}</>;
  }

  return <SingleProductPage productId={productId} />;
};

export default SingleProductGate;
